import React, { useState, useEffect, useMemo } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import BaseChart from './BaseChart';
import { useTranslation } from 'react-i18next';

// Expected format: { date: { shift: { eventCode: count } } }
type NumberOfRejectEventsData = Record<string, Record<string, Record<string, number>>>;

interface NumberOfRejectEventsChartProps {
  machineSerialNo: string;
  lineId?: string;
  shifts: string[];
  showAll?: boolean; // Whether to show all events or just top 10
}

const NumberOfRejectEventsChart: React.FC<NumberOfRejectEventsChartProps> = ({ machineSerialNo, shifts, showAll = false }) => {
  const { t } = useTranslation();
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [data, setData] = useState<NumberOfRejectEventsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use chart cache for better performance
  const chartCache = useChartCache<NumberOfRejectEventsData>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30 // Cache up to 30 different chart configurations
  });

  const fetchNumberOfRejectEventsData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) {
      setLoading(false);
      return;
    }
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    // Check cache first
    const cachedData = chartCache.getCachedData(
      'pc.number_of_reject_events',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      setData(cachedData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      console.log('📊 NumberOfRejectEventsChart: Making request with params:', params);
      const response = await ChartDataService.fetchNumberOfRejectEvents(params, token);
      console.log('📊 NumberOfRejectEventsChart: Got response:', response);

      if (response.success && response.data) {
        const responseData = response.data as any;
        console.log('📊 NumberOfRejectEventsChart: Response data:', responseData);
        
        // Extract nested data format: { date: { shift: { code: count } } }
        // Response is: { error: null, results: { "2016-01-19 00:00:00 +0000 UTC": { "A1": { ... } } } }
        let extractedData: NumberOfRejectEventsData | null = null;
        
        if (responseData.results && typeof responseData.results === 'object' && !Array.isArray(responseData.results)) {
          // Check if results has nested structure (date -> shift -> codes)
          const firstKey = Object.keys(responseData.results)[0];
          if (firstKey && typeof responseData.results[firstKey] === 'object') {
            extractedData = responseData.results;
          }
        } else if (typeof responseData === 'object' && !Array.isArray(responseData) && !responseData.error) {
          // Direct nested structure
          const firstKey = Object.keys(responseData)[0];
          if (firstKey && typeof responseData[firstKey] === 'object' && !Array.isArray(responseData[firstKey])) {
            extractedData = responseData;
          }
        }
        
        if (extractedData) {
          setData(extractedData);
          chartCache.setCachedData('pc.number_of_reject_events', machineSerialNo, dateRange, shifts, extractedData);
        } else {
          console.warn('NumberOfRejectEventsChart: Unexpected data format:', responseData);
          setError('Unexpected data format');
        }
      } else {
        setError(response.error || 'Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching number of reject events data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch number of reject events data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('📊 NumberOfRejectEventsChart: useEffect triggered', { 
      machineSerialNo, 
      dateRange, 
      shiftsLength: shifts?.length,
      hasToken: !!token 
    });
    fetchNumberOfRejectEventsData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts, token]);

  // Transform nested data into stacked column chart format
  const chartData = useMemo(() => {
    if (!data || typeof data !== 'object') {
      return { categories: [], series: [], eventCodeTotals: new Map<string, number>() };
    }

    // Build categories (date + shift) and collect all event codes with their totals
    const categories: string[] = [];
    const categoryMeta: { date: string; shift: string }[] = [];
    const eventCodeTotals = new Map<string, number>();
    const dataMatrix: Record<string, number[]> = {};

    // Helper to parse date from "2016-01-19 00:00:00 +0000 UTC" format
    const parseDate = (dateStr: string): string => {
      // Extract just the date part (YYYY-MM-DD)
      const match = dateStr.match(/^(\d{4}-\d{2}-\d{2})/);
      return match ? match[1] : dateStr.split(' ')[0];
    };

    // Get sorted dates (parse the date part for sorting)
    const dates = Object.keys(data).sort((a, b) => {
      const dateA = parseDate(a);
      const dateB = parseDate(b);
      return dateA.localeCompare(dateB);
    });

    // Build categories and collect event codes
    for (const dateKey of dates) {
      const shiftsData = data[dateKey];
      if (!shiftsData || typeof shiftsData !== 'object') continue;

      const displayDate = parseDate(dateKey);
      
      // Sort shifts (A1, B1, C1, etc.)
      const shiftKeys = Object.keys(shiftsData).sort();
      
      for (const shift of shiftKeys) {
        categories.push(`${displayDate} ${shift}`);
        categoryMeta.push({ date: displayDate, shift });
        
        const eventCodes = shiftsData[shift];
        if (!eventCodes || typeof eventCodes !== 'object') continue;

        for (const [code, count] of Object.entries(eventCodes)) {
          const numCount = typeof count === 'number' ? count : parseInt(count as string, 10) || 0;
          eventCodeTotals.set(code, (eventCodeTotals.get(code) || 0) + numCount);
        }
      }
    }

    // Sort event codes by total count descending
    const sortedCodes = Array.from(eventCodeTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([code]) => code);

    // Limit to top 10 if not showing all
    const displayCodes = showAll ? sortedCodes : sortedCodes.slice(0, 10);

    // Initialize data matrix for each code
    for (const code of displayCodes) {
      dataMatrix[code] = new Array(categories.length).fill(0);
    }

    // Fill data matrix
    let categoryIndex = 0;
    for (const dateKey of dates) {
      const shiftsData = data[dateKey];
      if (!shiftsData || typeof shiftsData !== 'object') continue;

      const shiftKeys = Object.keys(shiftsData).sort();
      
      for (const shift of shiftKeys) {
        const eventCodes = shiftsData[shift];
        if (!eventCodes || typeof eventCodes !== 'object') {
          categoryIndex++;
          continue;
        }

        for (const [code, count] of Object.entries(eventCodes)) {
          if (dataMatrix[code]) {
            const numCount = typeof count === 'number' ? count : parseInt(count as string, 10) || 0;
            dataMatrix[code][categoryIndex] = numCount;
          }
        }
        categoryIndex++;
      }
    }

    // Build series for each event code
    const series = displayCodes.map(code => ({
      name: code,
      data: dataMatrix[code]
    }));

    return { categories, series, eventCodeTotals, categoryMeta };
  }, [data, showAll]);

  // Check if we have valid data
  const hasEventsData = chartData.series.length > 0 && chartData.categories.length > 0;

  // Show loading state
  if (loading) {
    return (
      <div className="w-full">
        <BaseChart
          type="column"
          height={400}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">{t('charts.noShiftsSelected', 'No Shifts Selected')}</h3>
          <p className="theme-text-secondary">{t('charts.noShiftsMessage', 'No shifts selected or available for the selected timeframe')}</p>
        </div>
      </div>
    );
  }

  if (!hasEventsData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">{t('charts.noRejectEventsData', 'No Reject Events Data')}</h3>
          <p className="theme-text-secondary">{t('charts.noRejectEventsMessage', 'No reject events data found for the selected period.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BaseChart
        commentProps={{ chartType: 'number_of_reject_events', machineBK: machineSerialNo }}
        type="column"
        height={400}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          title: t('charts.numberOfEvents', 'Number of Events'),
          min: 0
        }}
        customOptions={{
          yAxis: {
            stackLabels: {
              enabled: true,
              style: {
                fontWeight: 'bold',
                color: 'gray'
              }
            }
          },
          plotOptions: {
            column: {
              stacking: 'normal',
              dataLabels: {
                enabled: false
              }
            }
          },
          xAxis: {
            labels: {
              rotation: -45,
              style: {
                fontSize: '10px'
              }
            }
          },
          tooltip: {
            formatter: function(this: any) {
              const point = this.point;
              const series = this.series;
              const total = this.total || 0;
              const category = (point as any).category || 
                               (this.series.xAxis?.categories && this.series.xAxis.categories[point.x as number]) ||
                               String(this.x ?? '');
              
              // Parse date and shift from category (e.g., "2023-01-12 A1")
              const parts = category.split(' ');
              const date = parts[0] || '';
              const shift = parts[1] || '';
              
              return `<b>Code:</b> ${series.name}<br/>` +
                     `<b>Count:</b> ${point.y} Events<br/>` +
                     `<b>Total:</b> ${total} Events<br/>` +
                     `<b>Shift:</b> ${shift}<br/>` +
                     `<b>Date:</b> ${date}`;
            }
          },
          legend: {
            enabled: true,
            layout: 'vertical',
            align: 'right',
            verticalAlign: 'middle',
            itemStyle: {
              fontSize: '11px'
            },
            maxHeight: 350
          }
        }}
        legend={{
          enabled: true
        }}
        loading={loading}
        error={error}
        onRetry={fetchNumberOfRejectEventsData}
        className="w-full"
      />
    </div>
  );
};

export default NumberOfRejectEventsChart;
